import React, { useEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  useBillingReconciliationCatalog,
  useMarkBillingReconciliationCatalogItemReviewed,
  useUpdateBillingReconciliationCatalogItem,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import { money } from './format.js';

const KNOWN_BASE_CODES = ['DI_DTRK_', 'DI_P1_', 'DI_B_', 'DI_OS_', 'DI_VS', 'DI_'];

const baseCodeForProductCode = (code = '') => {
  const normalized = String(code || '').trim().toUpperCase();
  const known = KNOWN_BASE_CODES.find((prefix) => normalized.startsWith(prefix));
  if (known && known !== 'DI_') return known;
  const match = normalized.match(/^([A-Z]+_[A-Z0-9]+_)/);
  return match?.[1] || normalized;
};

const pickImportValue = (row = {}, names = []) => {
  const entries = Object.entries(row);
  for (const name of names) {
    const found = entries.find(([key]) => String(key).trim().toLowerCase() === name);
    if (found && String(found[1] ?? '').trim()) return String(found[1]).trim();
  }
  return '';
};

const pricingTableImportMap = (rows = []) => rows.reduce((acc, row) => {
  const rawBase = pickImportValue(row, ['base code', 'basecode', 'base_code', 'product base', 'productbase'])
    || pickImportValue(row, ['product code', 'productcode', 'product_code', 'code']);
  const pricingTableId = pickImportValue(row, [
    'pricing table id',
    'pricingtableid',
    'pricing_table_id',
    'pricing table',
    'pricingtable',
  ]);
  const baseCode = baseCodeForProductCode(rawBase);
  if (baseCode && pricingTableId) acc.set(baseCode, pricingTableId);
  return acc;
}, new Map());

const CatalogRow = ({ item }) => {
  const [priceDraft, setPriceDraft] = useState(String(item.standardPrice ?? ''));
  const [pricingTableDraft, setPricingTableDraft] = useState(String(item.pricingTableId ?? ''));
  const update = useUpdateBillingReconciliationCatalogItem();
  const markReviewed = useMarkBillingReconciliationCatalogItemReviewed();

  useEffect(() => {
    setPriceDraft(String(item.standardPrice ?? ''));
    setPricingTableDraft(String(item.pricingTableId ?? ''));
  }, [item.pricingTableId, item.standardPrice]);

  const price = Number(priceDraft);
  const priceDirty = Number(item.standardPrice) !== price;
  const pricingTableDirty = String(item.pricingTableId ?? '') !== pricingTableDraft.trim();
  const dirty = priceDirty || pricingTableDirty;

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
      sx={{ p: 1.25, borderRadius: 1, border: '1px solid', borderColor: item.reviewed ? 'divider' : 'warning.main' }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {item.productCode}
          </Typography>
          {!item.reviewed && <Chip size="small" color="warning" label="Unreviewed" sx={{ height: 20, fontWeight: 700 }} />}
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {item.displayName || 'Unnamed product'} - {item.source || 'catalog'}
        </Typography>
      </Box>
      <TextField
        size="small"
        label="Standard price"
        value={priceDraft}
        onChange={(event) => setPriceDraft(event.target.value)}
        inputProps={{ inputMode: 'decimal' }}
        sx={{ width: { xs: '100%', md: 160 } }}
      />
      <TextField
        size="small"
        label="Pricing table ID"
        value={pricingTableDraft}
        onChange={(event) => setPricingTableDraft(event.target.value)}
        sx={{ width: { xs: '100%', md: 220 } }}
      />
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          disabled={!dirty || !Number.isFinite(price) || update.isPending}
          onClick={() => update.mutate({
            id: item.id,
            patch: {
              standardPrice: price,
              pricingTableId: pricingTableDraft.trim() || null,
            },
          })}
          sx={{ textTransform: 'none' }}
        >
          Save
        </Button>
        {!item.reviewed && (
          <Button
            size="small"
            variant="contained"
            disabled={markReviewed.isPending}
            onClick={() => markReviewed.mutate({ id: item.id })}
            sx={{ textTransform: 'none' }}
          >
            Mark reviewed
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const PricingCatalogPanel = ({ program, limit = 500 }) => {
  const fileInputRef = useRef(null);
  const [importMessage, setImportMessage] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const query = useBillingReconciliationCatalog({ program, limit });
  const bulkUpdate = useUpdateBillingReconciliationCatalogItem();
  const rows = useMemo(
    () => [...(query.data || [])].sort((left, right) => (
      Number(Boolean(left.reviewed)) - Number(Boolean(right.reviewed)) ||
      String(left.productCode || left.displayName || '').localeCompare(String(right.productCode || right.displayName || ''))
    )),
    [query.data],
  );
  const groupedRows = useMemo(() => rows.reduce((acc, row) => {
    const baseCode = baseCodeForProductCode(row.productCode);
    if (!acc.has(baseCode)) acc.set(baseCode, []);
    acc.get(baseCode).push(row);
    return acc;
  }, new Map()), [rows]);
  const unreviewed = rows.filter((row) => !row.reviewed).length;
  const visibleGroups = useMemo(() => [...groupedRows.entries()].sort(([left], [right]) => left.localeCompare(right)), [groupedRows]);

  useEffect(() => {
    setExpandedGroups((current) => {
      if (current.size > 0) return current;
      return new Set(visibleGroups.map(([baseCode]) => baseCode));
    });
  }, [visibleGroups]);

  const toggleGroup = (baseCode) => {
    setExpandedGroups((current) => {
      const next = new Set(current);
      if (next.has(baseCode)) next.delete(baseCode);
      else next.add(baseCode);
      return next;
    });
  };

  const handlePricingTableFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async ({ data, errors }) => {
        if (errors?.length) {
          setImportMessage({ severity: 'error', text: `Pricing table import failed: ${errors[0].message}` });
          return;
        }
        const tableMap = pricingTableImportMap(data);
        if (!tableMap.size) {
          setImportMessage({ severity: 'warning', text: 'No pricing table IDs were found. Use columns like base_code and pricing_table_id.' });
          return;
        }
        const updates = rows
          .map((row) => ({ row, pricingTableId: tableMap.get(baseCodeForProductCode(row.productCode)) }))
          .filter(({ row, pricingTableId }) => pricingTableId && String(row.pricingTableId || '') !== pricingTableId);
        if (!updates.length) {
          setImportMessage({ severity: 'info', text: 'No catalog rows needed pricing table ID changes from that file.' });
          return;
        }
        try {
          await Promise.all(updates.map(({ row, pricingTableId }) => (
            bulkUpdate.mutateAsync({ id: row.id, patch: { pricingTableId } })
          )));
          setImportMessage({ severity: 'success', text: `Applied pricing table IDs to ${updates.length} catalog row${updates.length === 1 ? '' : 's'}.` });
        } catch (error) {
          setImportMessage({ severity: 'error', text: 'Pricing table IDs could not be applied. Please retry the upload.' });
        }
      },
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SellOutlinedIcon color={unreviewed ? 'warning' : 'primary'} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Pricing Catalog
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Standard product pricing for new reconciliation runs
            </Typography>
          </Box>
          <Chip size="small" variant="outlined" label={`${rows.length} products`} sx={{ fontWeight: 700 }} />
          {unreviewed > 0 && <Chip size="small" color="warning" label={`${unreviewed} unreviewed`} sx={{ fontWeight: 700 }} />}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handlePricingTableFile}
            style={{ display: 'none' }}
          />
          <Tooltip title="Upload a CSV with base_code and pricing_table_id columns">
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={bulkUpdate.isPending || rows.length === 0}
              sx={{ textTransform: 'none' }}
            >
              Pricing IDs
            </Button>
          </Tooltip>
        </Stack>
        {importMessage && (
          <Alert severity={importMessage.severity} onClose={() => setImportMessage(null)}>
            {importMessage.text}
          </Alert>
        )}

        {query.isLoading ? (
          <Stack spacing={1}>
            <Skeleton height={44} />
            <Skeleton height={44} />
          </Stack>
        ) : query.isError ? (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={() => query.refetch()}>Retry</Button>}
          >
            Pricing catalog could not be loaded.
          </Alert>
        ) : visibleGroups.length > 0 ? (
          <Stack spacing={1}>
            {visibleGroups.map(([baseCode, groupRows]) => {
              const groupUnreviewed = groupRows.filter((row) => !row.reviewed).length;
              const isExpanded = expandedGroups.has(baseCode);
              return (
                <Box key={baseCode} sx={{ border: '1px solid', borderColor: groupUnreviewed ? 'warning.main' : 'divider', borderRadius: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    onClick={() => toggleGroup(baseCode)}
                    sx={{ cursor: 'pointer', px: 1.25, py: 1, bgcolor: 'action.hover' }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 900, fontFamily: 'monospace', minWidth: 120 }}>
                      {baseCode}
                    </Typography>
                    <Chip size="small" variant="outlined" label={`${groupRows.length} code${groupRows.length === 1 ? '' : 's'}`} sx={{ height: 20 }} />
                    {groupUnreviewed > 0 && <Chip size="small" color="warning" label={`${groupUnreviewed} unreviewed`} sx={{ height: 20, fontWeight: 700 }} />}
                  </Stack>
                  <Collapse in={isExpanded} unmountOnExit>
                    <Stack spacing={1} sx={{ p: 1 }}>
                      {groupRows.map((item) => <CatalogRow key={item.id || item.productCode} item={item} />)}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No catalog rows yet.
          </Typography>
        )}
        {rows.length > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Current reviewed prices start at {money(Math.min(...rows.map((row) => Number(row.standardPrice || 0))))}.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default PricingCatalogPanel;
